import { plural, t } from "@lingui/core/macro"
import { parsedResult } from "@multimediallc/web-utils/messageParsing"
import { MessagePartType } from "@multimediallc/web-utils/types"
import { useAppContext } from "../../../hooks/appContext"
import { draftCache } from "../DraftCache/DraftCache"
import styles from "./MessagePreview.module.scss"
import type { Thread } from "../types"
import type { IEmoticon, IStringPart } from "@multimediallc/web-utils/types"

const MAX_MESSAGE_DISPLAY_COUNT = 99

/** Converts a message with emoticons into an array of text and emoticon shortcodes, truncating the first text part at its first newline */
export function parseMessagePreview(message: string): (string | null)[] {
    const parts = parsedResult(message)
    return parts.map((part, i) => {
        if (part.partType === MessagePartType.string) {
            const stringPart = part as IStringPart
            return i === 0 ? stringPart.s.split("\n")[0] : stringPart.s
        } else if (part.partType === MessagePartType.emoticon) {
            const emoticon = part as IEmoticon
            return `:${emoticon.name}`
        }
        return null
    })
}

type Props = {
    thread: Thread
    isTyping: boolean
}

const TipMessage = ({
    thread,
    tipAmount,
}: {
    thread: Thread
    tipAmount: number
}) => {
    const sender = thread.other_user.username
    const tokenText = plural(tipAmount, {
        one: `${tipAmount} token`,
        other: `${tipAmount} tokens`,
    })

    if (thread.from_user === thread.other_user.username) {
        return <span>{t`${sender} tipped ${tokenText}`}</span>
    }
    return <span>{t`You tipped ${tokenText}`}</span>
}

export function MessagePreview({ thread, isTyping }: Props) {
    const fromUser = thread.from_user
    const otherUser = thread.other_user.username
    const loggedInUsername = useAppContext().context.logged_in_user?.username
    const draft = draftCache.get(thread.other_user.username)
    const unread = thread.num_unread
    const hasTip = !!thread.tip_amount
    const tipAmount = thread.tip_amount ?? 0

    // Priority 1: Typing indicator
    if (isTyping) {
        return <span>{t`${otherUser} is typing...`}</span>
    }

    // Priority 2: New messages
    if (unread > 0) {
        if (hasTip) {
            return <TipMessage thread={thread} tipAmount={tipAmount} />
        }

        if (unread < 2) {
            const isLastMsgByUser = fromUser === loggedInUsername
            const messageText = parseMessagePreview(thread.m)

            return isLastMsgByUser ? (
                <span>
                    {t`You:`} {messageText}
                </span>
            ) : (
                <span>{messageText}</span>
            )
        } else {
            const unreadText =
                unread > MAX_MESSAGE_DISPLAY_COUNT
                    ? t`${MAX_MESSAGE_DISPLAY_COUNT}+ new messages`
                    : t`${unread} new messages`
            return <span>{unreadText}</span>
        }
    }

    // Priority 3: Draft (only if new messages are absent)
    if (draft && draft.message) {
        return (
            <span>
                <span className={styles.draftPretext}>{t`Draft:`}</span>{" "}
                {draft.message}
            </span>
        )
    }

    // Priority 4: Read message previews (only when no new messages or drafts)
    if (hasTip) {
        return <TipMessage thread={thread} tipAmount={tipAmount} />
    }

    if (thread.m) {
        const isLastMsgByUser = fromUser === loggedInUsername
        const messageText = parseMessagePreview(thread.m)

        return isLastMsgByUser ? (
            <span>
                {t`You:`} {messageText}
            </span>
        ) : (
            <span>{messageText}</span>
        )
    }

    // Ghost draft with no message
    if (draft && !draft.message && !thread.m) {
        return <span />
    }

    return <span />
}
